import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid } from '@mui/material';
import { driver } from 'driver.js';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import { DialogAnimate } from 'src/components/animate';
import Avatar from 'src/components/Avatar';
import { GeneralDatePicker, GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { SkeletonForm } from 'src/components/skeleton';
import { APP_DUAL_LIST } from 'src/constants/queryKeys';
import useFormSubmissionHandler from 'src/hooks/useFormSubmissionHander';
import { networkCreateDuel, networkUpdateDuel, useFetchDuelGroup } from 'src/network/duels';
import { ContentSelectionTableModal } from 'src/sections/@dashboard/project/feedItems';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import * as Yup from 'yup';
import { getDefaultValues } from './defaultValues';
import { SCHEMA1, SCHEMA2, SCHEMA3, SCHEMA4 } from './validationSchema';

export default function DuelForm() {
  const { modal, closeModal } = useFormsModalStore();
  const formSubmissionHandler = useFormSubmissionHandler();

  const { appInfo } = useAppInfoStore();

  const { projectId } = useParams();

  const duelDetails = modal?.data;
  const isEditMode = !!duelDetails;

  const [isLoading, setIsLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [gameDetails, setGameDetails] = useState(duelDetails);

  const [startDate, setStartDate] = useState(new Date(duelDetails?.DuelCreateTime));
  const [endDate, setEndDate] = useState(new Date(duelDetails?.DuelExpireTime));

  const [isIncludePayment, setIsIncludePayment] = useState(false);

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [popupTypes, setPopupTypes] = useState([]);
  const [selectedPopupType, setSelectedPopupType] = useState('');

  const [duelGroups, setDuelGroups] = useState([]);
  const [selectedDuelGroup, setSelectedDuelGroup] = useState('');

  // const [gamesList, setGamesList] = useState([]);

  const { data: duelGroupData, isLoading: isLoadingData } = useFetchDuelGroup(0, 100, projectId);
  // const { data: gamesListData, isLoading: gamesListIsLoading, isFetching: gamesListIsFetching } = useFetchGamesList({}, 0, 40);

  const mainSchema = Yup.object().shape({
    ...SCHEMA1.fields,
    ...(isIncludePayment && SCHEMA2.fields),
    ...(selectedPopupType === 1 && SCHEMA3.fields),
    ...(selectedPopupType === 2 && SCHEMA4.fields),
  });

  const defaultValues = getDefaultValues(duelDetails);

  const methods = useForm({
    // resolver: yupResolver(mainSchema), // todo schema
    defaultValues,
  });

  const {
    reset,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
  } = methods;

  const includePayment = useWatch({
    control,
    name: 'DuelJoinNeedPayment',
  });
  const onSubmit = async (values, event) => {
    const payload = {
      ...values,
      DuelApplicationID: +projectId,
      DuelTransactionAmountTypeID: +selectedAmountType || amountTypes?.[0]?.TransactionAmountTypeLable,
      DuelPopupTypeID: +selectedPopupType || popupTypes?.[0]?.Value,
      DuelTitle: values?.DuelTitle,
      DuelImageBanner: values?.DuelImageBanner,
      DuelPopupImageFile: values?.DuelPopupImageFile || '',
      PopupURL: values?.PopupURL || '',
      DuelCreateTime: new Date(startDate).toISOString(),
      DuelExpireTime: new Date(endDate).toISOString(),
      DuelMinimumScore: values?.DuelMinimumScore,
      DuelIsPrivate: 0,
      DuelJoinNeedPayment: +values?.DuelJoinNeedPayment,
      DuelAmount: +values?.DuelAmount,
      DuelGameID: gameDetails?.GameID,
      DuelTermsAgreement: values?.DuelTermsAgreement,
      DuelOrderBy: +values?.DuelOrderBy,
      IsActive: values?.IsActive ? 1 : 2
    };

    try {
      setIsLoading(true);
      if (isEditMode) {
        formSubmissionHandler({
          event,
          reset,
          payload,
          closeModal,
          apiCall: () => networkUpdateDuel(duelDetails.DuelID, payload),
          itemID: duelDetails.DuelID,
          queryKey: [APP_DUAL_LIST, projectId],
          successMessage: 'تورنومنت موردنظر با موفقیت ویرایش شد',
        });
      } else {
        formSubmissionHandler({
          event,
          reset,
          payload,
          closeModal,
          apiCall: () => networkCreateDuel(payload),
          queryKey: [APP_DUAL_LIST, projectId],
          successMessage: 'تورنومنت موردنظر با موفقیت ایجاد شد',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectContentHandler = (item) => {
    setIsOpen(false);
    setGameDetails(item);
    setValue('selectedGame', item?.GameName);
  };

  useEffect(() => {
    if (includePayment) setIsIncludePayment(true);
    else setIsIncludePayment(false);
  }, [includePayment]);

  useEffect(() => {
    const groupItems = duelGroupData?.data?.items;
    setDuelGroups(groupItems);
    setSelectedDuelGroup(groupItems?.[0]?.DuelGroupID);
  }, []);

  // useEffect(() => {
  //   const gamesList = gamesListData?.data?.items || [];
  //   setGamesList(gamesList);
  //   // if (!isEditMode) setValue('selectedGame', gamesList[0]?.GameName);
  // }, [gamesListIsLoading, gamesListIsFetching]);

  useEffect(() => {
    const popupTypeList = generateSelectMenuItems(appInfo?.DuelPopupType, 'Title', 'Value');
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    setPopupTypes(popupTypeList);
    setSelectedPopupType(duelDetails?.DuelPopupTypeID || popupTypeList[0]?.id);

    setAmountTypes(amountTypeList);
    setSelectedAmountType(duelDetails?.TransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  const driverInstance = driver({
    popoverClass: 'driverjs-theme',
    stagePadding: 0,
    overlayColor: 'transparent',
    onDestroyed: () => {
      document?.activeElement?.blur();
    },
  });

  const isShowLoading = isLoading || isLoadingData;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {isShowLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={12} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField name="DuelTitle" type="text" label="عنوان تورنومنت" />

                  <RHFTextField name="DuelMinimumScore" type="text" label="حداقل امتیاز کاربر برای ورود" />

                  <RHFTextField
                    name="selectedGame"
                    type="text"
                    label="بازی"
                    InputProps={{
                      endAdornment: (
                        <>
                          {gameDetails ? (
                            <Avatar
                              sx={{ cursor: 'pointer' }}
                              alt={gameDetails?.GameName}
                              src={gameDetails?.GameLogo || '/images/fallback.jpeg'}
                              onClick={() => setIsOpen(true)}
                            />
                          ) : (
                            <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                              انتخاب
                            </Button>
                          )}
                        </>
                      ),
                    }}
                  />

                  <RHFTextField name="DuelOrderBy" type="text" label="ترتیب نمایش" />

                  {duelGroups?.length > 0 ? (
                    <GeneralSelectMenu
                      label="تورنومنت گروهی"
                      name="DuelPopupTypeID"
                      options={duelGroups}
                      value={selectedDuelGroup}
                      onChangeValue={setSelectedDuelGroup}
                    />
                  ) : null}

                  <GeneralDatePicker
                    minDate={new Date()}
                    label="تاریخ شروع"
                    name="DuelCreateTime"
                    value={startDate}
                    onChangeValue={setStartDate}
                  />

                  <GeneralDatePicker
                    minDate={startDate}
                    label="تاریخ پایان"
                    name="DuelExpireTime"
                    value={endDate}
                    onChangeValue={setEndDate}
                  />

                  <GeneralUpload
                    label="عکس بنر"
                    id="duel_image_banner"
                    name="DuelImageBanner"
                    afterChangeAction={(res) => {
                      setValue('DuelImageBanner', res);
                      trigger('DuelImageBanner');
                    }}
                    onFocus={(e) =>
                      driverInstance.highlight({
                        element: e?.target,
                        popover: {
                          title: 'فیلد وردی آپلود عکس بنر',
                          description:
                            'لطفاً تصویری با نسبت 16:9 آپلود کنید. این تصویر باید به صورت افقی باشد و عرض آن باید نسبت به ارتفاع 16 برابر باشد. برای بهترین نتیجه، از تصاویری با کیفیت بالا استفاده کنید.',
                        },
                      })
                    }
                  />

                  <GeneralSelectMenu
                    label="نوع اعلان"
                    name="DuelPopupTypeID"
                    options={popupTypes}
                    value={selectedPopupType}
                    onChangeValue={setSelectedPopupType}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    mt: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: `repeat(${selectedPopupType === 1 ? 1 : 2}, 1fr)`,
                    },
                  }}
                >
                  {selectedPopupType === 1 ? (
                    <>
                      <RHFTextField name="DuelTermsAgreement" multiline rows={6} label="توضیحات" maxCharacters={1500} />
                      {duelGroups?.length === 0 ? <div /> : null}
                    </>
                  ) : selectedPopupType === 2 ? (
                    <>
                      <GeneralUpload
                        label="تصویر اعلان"
                        id="duel_popup_image"
                        name="DuelPopupImageFile"
                        afterChangeAction={(res) => {
                          setValue('DuelPopupImageFile', res);
                          trigger('DuelPopupImageFile');
                        }}
                      />
                      <RHFTextField name="PopupURL" type="text" label="آدرس ارجاع بر روی اعلان" />
                    </>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    my: 2,
                  }}
                >
                  <RHFSwitch name="DuelJoinNeedPayment" labelPlacement="end" label="شامل پرداخت شود؟" />

                  <RHFSwitch name="IsActive" labelPlacement="end" label="نمایش داده شود؟" />

                  {includePayment ? (
                    <>
                      <GeneralSelectMenu
                        label="نوع واحد پرداخت"
                        name="DuelTransactionAmountTypeID"
                        options={amountTypes}
                        value={selectedAmountType}
                        onChangeValue={setSelectedAmountType}
                      />

                      <RHFTextField name="DuelAmount" type="text" label="هزینه قابل پرداخت" />
                    </>
                  ) : null}
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {!isEditMode ? 'ذخیره و ایجاد بعدی ' : 'ذخیره و ادامه ویرایش'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>انتخاب بازی</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <ContentSelectionTableModal selectContentHandler={selectContentHandler} />
        </div>
      </DialogAnimate>
    </FormProvider>
  );
}
